/* facets-column.js
 * Desktop-only control for the collection filter column (A/B variant).
 * Lets shoppers switch between the persistent left column and the existing
 * filter drawer. The preference is remembered per browser.
 */
(() => {
  const STORAGE_KEY = 'collection:desktop-filter-layout';
  const LAYOUTS = ['column', 'drawer'];

  function readStoredLayout() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return LAYOUTS.includes(stored) ? stored : null;
    } catch (e) {
      return null;
    }
  }

  function storeLayout(layout) {
    try {
      localStorage.setItem(STORAGE_KEY, layout);
    } catch (e) {
      /* storage unavailable — preference is session-only */
    }
  }

  function applyLayout(root, layout) {
    root.setAttribute('data-filter-layout', layout);

    root.querySelectorAll('[data-facets-column-toggle]').forEach((button) => {
      const isColumn = layout === 'column';
      const label = button.querySelector('[data-facets-column-toggle-label]');
      button.setAttribute('aria-expanded', String(isColumn));
      if (label) {
        label.textContent = isColumn ? button.dataset.labelHide : button.dataset.labelShow;
      }
    });

    document.dispatchEvent(
      new CustomEvent('collection:filter-layout', { detail: { layout } })
    );
  }

  function init(root) {
    if (!root || root.dataset.filterColumnReady === 'true') return;
    root.dataset.filterColumnReady = 'true';

    const defaultLayout = LAYOUTS.includes(root.dataset.filterColumnDefault)
      ? root.dataset.filterColumnDefault
      : 'column';

    applyLayout(root, readStoredLayout() || defaultLayout);
    watchPromoTiles(root);
    keepFiltersExpanded(root);

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-facets-column-toggle]');
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      const next = root.getAttribute('data-filter-layout') === 'column' ? 'drawer' : 'column';
      storeLayout(next);
      applyLayout(root, next);
    });
  }

  /* Every filter group renders expanded. facets.js re-renders group contents on
   * each update, so re-assert that state afterwards while honouring any group
   * the shopper collapsed themselves.
   */
  function keepFiltersExpanded(root) {
    const column = root.querySelector('[data-facets-column]');
    if (!column) return;

    const expandAll = () => {
      column.querySelectorAll('.facets-column__details').forEach((details) => {
        if (details.dataset.userCollapsed !== 'true') details.open = true;
      });
    };

    // `toggle` does not bubble, so listen during the capture phase.
    column.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!details.classList || !details.classList.contains('facets-column__details')) return;
        details.dataset.userCollapsed = String(!details.open);
      },
      true
    );

    expandAll();
    new MutationObserver(expandAll).observe(column, { childList: true, subtree: true });
  }

  /* Touts and section breaks are auto-placed in this layout (see
   * component-facets-column.css), which would drop them at the end of the list.
   * Their authored row/column coordinates assume a 3-column grid, so convert
   * those back into a flex `order` to restore the intended reading position.
   * Only inline styles are written, so the grid's own MutationObservers are
   * never triggered.
   */
  const AUTHORED_COLUMNS = 3;

  function isPromoTile(item) {
    return item.classList.contains('image-tout') || item.classList.contains('section-break');
  }

  function orderPromoTiles(container) {
    const list = container.querySelector('#product-grid');
    if (!list) return;

    const items = Array.from(list.children);
    if (!items.some(isPromoTile)) return;

    let productSlot = 0;

    items.forEach((item) => {
      if (isPromoTile(item)) return;
      productSlot += 1;
      item.style.order = productSlot * 10;
    });

    items.forEach((item) => {
      if (!isPromoTile(item)) return;
      const row = parseInt(item.style.getPropertyValue('--desktop-row-start'), 10) || 1;
      const column = parseInt(item.style.getPropertyValue('--desktop-col-start'), 10) || 1;
      const slot = (row - 1) * AUTHORED_COLUMNS + column;
      item.style.order = slot * 10 - 5;
    });
  }

  function watchPromoTiles(root) {
    const container = root.querySelector('#ProductGridContainer');
    if (!container) return;

    orderPromoTiles(container);
    new MutationObserver(() => orderPromoTiles(container)).observe(container, {
      childList: true,
      subtree: true,
    });
  }

  function initAll() {
    document.querySelectorAll('.collection-grid--has-filter-column').forEach(init);
  }

  // The drawer and the column each render a "hide out of stock" toggle; keep them in sync.
  function mirrorAvailabilityToggles(source) {
    document
      .querySelectorAll('input[type="checkbox"][name="filter.v.availability"][value="1"]')
      .forEach((input) => {
        if (input !== source) input.checked = source.checked;
      });
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches && input.matches('input[type="checkbox"][name="filter.v.availability"][value="1"]')) {
      mirrorAvailabilityToggles(input);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
